// @contexts/PaymentClient.context.tsx

import React, { Suspense, createContext, useContext, ReactNode, useMemo } from 'react';
import { useLocation } from "react-router-dom";
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import type { StateRead } from "@ps-bridge/capabilitiesTypes";
import type { PaymentClient } from '@services/clients/PaymentClient';
import { fromInteropPaymentClient } from '@services/clients/PaymentClient';
import { createDefaultPaymentClient } from "@src/purescript/interop";
import { StubPaymentClient } from '@services/stubs/StubPaymentClient';
import { enabledFeatures, FeaturesEnum } from '@src/utils/enabledFeatures';
import { useJwtController } from '@contexts/Jwt.context';
import EmptyView from '@view/EmptyView';
import { requireContext } from '@contexts/requireContext';

export type PaymentProviderType = "stripe" | "square";

const PaymentClientContext = createContext<OptionTs<PaymentClient>>(Option.none());
const PaymentProviderContext = createContext<OptionTs<StateRead<PaymentProviderType>>>(Option.none());

export const PaymentClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const location = useLocation();
    const { markUnauthorized } = useJwtController();
    const paymentProvider: PaymentProviderType = (import.meta.env.VITE_PAYMENT_PROVIDER ?? "square") === "stripe"
        ? "stripe"
        : "square";
    const StripeProvider = React.lazy(() => import("./payment/StripeProvider"));
    const SquareProvider = React.lazy(() => import("./payment/SquareProvider"));
    const paymentProviderRead = useMemo(
        () => paymentProvider as StateRead<PaymentProviderType>,
        [paymentProvider],
    );

    const paymentClient = useMemo<PaymentClient>(() => {
        if (enabledFeatures[FeaturesEnum.StubClients]) {
            return new StubPaymentClient();
        }
        return fromInteropPaymentClient(createDefaultPaymentClient(markUnauthorized));
    }, [markUnauthorized]);

    const paymentClientValue = useMemo(
        () => Option.some(paymentClient),
        [paymentClient],
    );
    const paymentProviderValue = useMemo(
        () => Option.some(paymentProviderRead),
        [paymentProviderRead],
    );
    const isAuthRoute = useMemo(() => {
        const pathname = location.pathname;
        return (
            pathname === "/login" ||
            pathname === "/create-account" ||
            pathname === "/reset-password" ||
            pathname === "/reset-password/complete" ||
            pathname === "/account/verify" ||
            pathname === "/invite/accept"
        );
    }, [location.pathname]);

    const renderAuthRouteContent = (): ReactNode => {
        if (!(paymentProvider === "square" && location.pathname === "/create-account")) {
            return children;
        }

        return (
            <Suspense fallback={<EmptyView />}>
                <SquareProvider>
                    {children}
                </SquareProvider>
            </Suspense>
        );
    };

    if (isAuthRoute) {
        return (
            <PaymentClientContext.Provider value={paymentClientValue}>
                <PaymentProviderContext.Provider value={paymentProviderValue}>
                    {renderAuthRouteContent()}
                </PaymentProviderContext.Provider>
            </PaymentClientContext.Provider>
        );
    }

    if (paymentProvider === "stripe") {
        return (
            <PaymentClientContext.Provider value={paymentClientValue}>
                <PaymentProviderContext.Provider value={paymentProviderValue}>
                    <Suspense fallback={<EmptyView />}>
                        <StripeProvider paymentClient={paymentClient}>
                            {children}
                        </StripeProvider>
                    </Suspense>
                </PaymentProviderContext.Provider>
            </PaymentClientContext.Provider>
        );
    }

    return (
        <PaymentClientContext.Provider value={paymentClientValue}>
            <PaymentProviderContext.Provider value={paymentProviderValue}>
                <Suspense fallback={<EmptyView />}>
                    <SquareProvider>
                        {children}
                    </SquareProvider>
                </Suspense>
            </PaymentProviderContext.Provider>
        </PaymentClientContext.Provider>
    );
};

export const usePaymentClient = (): PaymentClient => {
    return requireContext(
        useContext(PaymentClientContext),
        'usePaymentClient must be used within a PaymentClientProvider',
    );
};

export const usePaymentProvider = (): StateRead<PaymentProviderType> => {
    return requireContext(
        useContext(PaymentProviderContext),
        'usePaymentProvider must be used within a PaymentClientProvider',
    );
};
